/**
 * Hero Banner Section
 * Industry-specific hero layouts
 */

import React from 'react';
import { Link } from 'react-router-dom';
import useDarkModeNeutrals from '../../hooks/useDarkModeNeutrals';
import useStorefrontTheme from '../../hooks/useStorefrontTheme';
import {
  GeneralIntegratedHero,
  FashionIntegratedHero,
  FmcgIntegratedHero,
  KidsIntegratedHero,
} from './IntegratedHero';
import './sections.css';

function HeroBanner({ settings = {}, content = {}, globalStyles = {}, store, industry }) {
  const {
    height = '500px',
    overlayOpacity = 0.3,
    overlayColor = '#000000',
    darkOverlayOpacity,
    darkOverlayColor,
    textAlign = 'center',
    verticalAlign = 'center',
    variant = 'default',
    // Granular style settings
    titleColor,
    subtitleColor,
    buttonBgColor,
    buttonTextColor,
    darkTitleColor,
    darkSubtitleColor,
    darkButtonBgColor,
    darkButtonTextColor,
    buttonStyle = 'filled',
    // Pill CTA is the new default — matches the storefront-wide
    // CTA shape (StorefrontHero, BottomNav, Header). Merchants can
    // still pin a different radius via theme settings.
    buttonRadius = '999px',
  } = settings;

  const {
    title = 'Welcome to Our Store',
    subtitle = 'Discover amazing products',
    buttonText = 'Shop Now',
    buttonLink = '/shop',
    backgroundImage = '',
  } = content;

  const { resolvedTheme } = useStorefrontTheme();
  const isDark = resolvedTheme === 'dark';
  const baseColors = globalStyles.colors || {};
  const colors = isDark ? { ...baseColors, ...(globalStyles.darkColors || {}) } : baseColors;
  const fonts = globalStyles.fonts || {};
  const resolvedTitleColor = (isDark ? darkTitleColor : titleColor) || (isDark ? colors.text || 'var(--theme-dark-text, #f3f4f6)' : '#ffffff');
  const resolvedSubtitleColor = (isDark ? darkSubtitleColor : subtitleColor) || (isDark ? colors.muted || 'var(--theme-dark-muted, #9ca3af)' : '#e5e5e5');
  const resolvedButtonBgColor = (isDark ? darkButtonBgColor : buttonBgColor) || colors.primary || '#2563eb';
  const resolvedButtonTextColor = (isDark ? darkButtonTextColor : buttonTextColor) || (isDark ? colors.background || 'var(--theme-dark-background, #0b0f17)' : '#ffffff');

  // Determine variant based on industry
  const effectiveVariant = variant !== 'default' ? variant : getIndustryVariant(industry);

  const heroProps = {
    height,
    overlayOpacity: isDark && darkOverlayOpacity !== undefined ? darkOverlayOpacity : overlayOpacity,
    overlayColor: isDark ? (darkOverlayColor || overlayColor) : overlayColor,
    textAlign,
    verticalAlign,
    title,
    subtitle,
    buttonText,
    buttonLink,
    backgroundImage,
    colors,
    fonts,
    globalStyles,
    // Pass granular style settings
    titleColor: resolvedTitleColor,
    subtitleColor: resolvedSubtitleColor,
    buttonBgColor: resolvedButtonBgColor,
    buttonTextColor: resolvedButtonTextColor,
    buttonStyle,
    buttonRadius,
  };

  // Integrated heroes consume a different prop surface (raw
   // settings/content/globalStyles/store) rather than the flattened
   // heroProps — they handle their own content adapter. Forwarding
   // both is harmless to the legacy heroes since they ignore the
   // extra props they don't destructure.
  const integratedProps = { settings, content, globalStyles, store, industry };

  switch (effectiveVariant) {
    // M5 — industry-specific integrated card heroes (new default
    // path for the FASHION/FMCG/KIDS/SERVICES/HOME_SERVICES/GENERAL
    // bucket of industries). Each renders the same integrated-card
    // shape as the storefront-default StorefrontHero, tuned for the
    // industry's character.
    case 'integrated-general':
      return <GeneralIntegratedHero {...integratedProps} />;
    case 'integrated-fashion':
      return <FashionIntegratedHero {...integratedProps} />;
    case 'integrated-fmcg':
      return <FmcgIntegratedHero {...integratedProps} />;
    case 'integrated-kids':
      return <KidsIntegratedHero {...integratedProps} />;

    // Specialised opt-in variants — kept as-is. Merchants who
    // explicitly picked Editorial/Minimal/Dark/Playful/Luxe/Warm
    // through the theme editor still get those distinct layouts.
    case 'editorial':
      return <EditorialHero {...heroProps} />;
    case 'minimal':
      return <MinimalHero {...heroProps} />;
    case 'dark':
      return <DarkHero {...heroProps} />;
    case 'playful':
      return <PlayfulHero {...heroProps} />;
    case 'luxe':
      return <LuxeHero {...heroProps} />;
    case 'warm':
      return <WarmHero {...heroProps} />;

    // Legacy 'default' fallback — kept for backwards compatibility
    // with any merchant config that explicitly set variant: 'default'
    // before the M5 routing landed. Routes through GeneralIntegratedHero
    // so they pick up the new look automatically.
    default:
      return <GeneralIntegratedHero {...integratedProps} />;
  }
}

function getIndustryVariant(industry) {
  // Industries map to either an integrated card hero (port of the
  // default StorefrontHero pattern, tuned per industry) or to a
  // specialised variant for industries where the existing
  // editorial/luxe/dark/warm aesthetic is the deliberate pick.
  //
  // The integrated heroes are: integrated-general, integrated-fashion,
  // integrated-fmcg, integrated-kids — each builds on a shared
  // <IntegratedHeroFrame> primitive in IntegratedHero.js.
  //
  // Specialised variants left intact: TECH/AUTOS use the dark hero
  // (dark backdrop + neon grid), LUXURY/REAL_ESTATE/BEAUTY use the
  // luxe hero (serif + gold dividers), RESTAURANT uses the warm hero
  // (warm tones, food-focused). These were chosen for character and
  // shouldn't be replaced by a generic integrated card.
  const industryMap = {
    FASHION: 'integrated-fashion',
    FMCG: 'integrated-fmcg',
    TECH: 'dark',
    KIDS: 'integrated-kids',
    LUXURY: 'luxe',
    RESTAURANT: 'warm',
    AUTOS: 'dark',
    REAL_ESTATE: 'luxe',
    HOME_SERVICES: 'integrated-general',
    BEAUTY: 'luxe',
    SERVICES: 'integrated-general',
    GENERAL: 'integrated-general',
  };
  return industryMap[industry] || 'integrated-general';
}


// Fashion Editorial Hero - Split layout, minimal text
function EditorialHero({ height, title, subtitle, buttonText, buttonLink, backgroundImage, fonts, titleColor, subtitleColor, buttonBgColor, buttonTextColor, buttonStyle, buttonRadius }) {
  const buttonStyleObj = { borderRadius: buttonRadius };
  if (buttonStyle === 'outline') {
    buttonStyleObj.backgroundColor = 'transparent';
    buttonStyleObj.border = `2px solid ${buttonBgColor}`;
    buttonStyleObj.color = buttonBgColor;
  } else if (buttonStyle === 'ghost') {
    buttonStyleObj.backgroundColor = 'transparent';
    buttonStyleObj.color = buttonBgColor;
  } else {
    buttonStyleObj.backgroundColor = buttonBgColor;
    buttonStyleObj.color = buttonTextColor;
  }

  return (
    <section className="ts-hero-banner ts-hero-banner--editorial" style={{ minHeight: height }}>
      <div className="ts-hero-editorial__grid">
        <div className="ts-hero-editorial__content">
          <span className="ts-hero-editorial__tag">New Collection</span>
          <h1 className="ts-hero-editorial__title" style={{ fontFamily: fonts.heading, color: titleColor }}>
            {title}
          </h1>
          {subtitle && <p className="ts-hero-editorial__subtitle" style={{ color: subtitleColor }}>{subtitle}</p>}
          {buttonText && (
            <Link to={buttonLink} className="ts-hero-editorial__button" style={buttonStyleObj}>
              {buttonText}
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          )}
        </div>
        <div className="ts-hero-editorial__image-wrapper">
          {backgroundImage ? (
            <img src={backgroundImage} alt="" className="ts-hero-editorial__image" />
          ) : (
            <div className="ts-hero-editorial__placeholder" />
          )}
        </div>
      </div>
    </section>
  );
}

// FMCG Minimal Hero - Smaller, functional
//
// titleColor/subtitleColor default to the brand-image-on-photo
// values (#ffffff/#e5e5e5) because this component is reused for
// other variants where text sits on a photo. The minimal layout
// is text-on-neutral, so when those defaults come through we fall
// back to the dark-mode-aware neutrals from useDarkModeNeutrals
// instead of hardcoded #111827/#6b7280 (which were invisible on
// the dark surface flip).
function MinimalHero({ title, subtitle, buttonText, buttonLink, backgroundImage, colors, titleColor, subtitleColor, buttonBgColor, buttonTextColor, buttonStyle, buttonRadius }) {
  const neutrals = useDarkModeNeutrals();
  const buttonStyleObj = { borderRadius: buttonRadius };
  if (buttonStyle === 'outline') {
    buttonStyleObj.backgroundColor = 'transparent';
    buttonStyleObj.border = `2px solid ${buttonBgColor}`;
    buttonStyleObj.color = buttonBgColor;
  } else if (buttonStyle === 'ghost') {
    buttonStyleObj.backgroundColor = 'transparent';
    buttonStyleObj.color = buttonBgColor;
  } else {
    buttonStyleObj.backgroundColor = buttonBgColor;
    buttonStyleObj.color = buttonTextColor;
  }

  return (
    <section className="ts-hero-banner ts-hero-banner--minimal">
      <div className="ts-hero-minimal__wrapper">
        <div className="ts-hero-minimal__image-side">
          {backgroundImage ? (
            <img src={backgroundImage} alt="" className="ts-hero-minimal__image" />
          ) : (
            <div className="ts-hero-minimal__placeholder" style={{ backgroundColor: buttonBgColor + '20' }} />
          )}
        </div>
        <div className="ts-hero-minimal__content">
          <h1 className="ts-hero-minimal__title" style={{ color: titleColor !== '#ffffff' ? titleColor : neutrals.text }}>{title}</h1>
          {subtitle && <p className="ts-hero-minimal__subtitle" style={{ color: subtitleColor !== '#e5e5e5' ? subtitleColor : neutrals.textMuted }}>{subtitle}</p>}
          {buttonText && (
            <Link to={buttonLink} className="ts-hero-minimal__button" style={buttonStyleObj}>
              {buttonText}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

// Tech Dark Hero - Dark with neon accents
function DarkHero({ height, title, subtitle, buttonText, buttonLink, backgroundImage, colors, fonts, titleColor, subtitleColor, buttonBgColor, buttonTextColor, buttonStyle, buttonRadius }) {
  const accentColor = buttonBgColor || colors.accent || '#00ff88';
  const buttonStyleObj = { '--accent': accentColor, borderRadius: buttonRadius };

  return (
    <section className="ts-hero-banner ts-hero-banner--dark" style={{ minHeight: height }}>
      <div className="ts-hero-dark__bg">
        {backgroundImage && <img src={backgroundImage} alt="" className="ts-hero-dark__bg-image" />}
        <div className="ts-hero-dark__grid" />
      </div>
      <div className="ts-hero-dark__content">
        <h1 className="ts-hero-dark__title" style={{ fontFamily: fonts.heading, color: titleColor }}>
          {title}
        </h1>
        {subtitle && <p className="ts-hero-dark__subtitle" style={{ color: subtitleColor }}>{subtitle}</p>}
        {buttonText && (
          <Link
            to={buttonLink}
            className="ts-hero-dark__button"
            style={buttonStyleObj}
          >
            {buttonText}
          </Link>
        )}
      </div>
      <div className="ts-hero-dark__glow" style={{ backgroundColor: accentColor }} />
    </section>
  );
}

// Kids Playful Hero - Colorful, fun shapes
//
// Same dark-mode-neutral fallback pattern as MinimalHero — text
// sits on a neutral surface, not on a photo, so the brand-default
// #ffffff title color is the wrong fallback in dark mode.
function PlayfulHero({ title, subtitle, buttonText, buttonLink, backgroundImage, colors, titleColor, subtitleColor, buttonBgColor, buttonTextColor, buttonStyle, buttonRadius }) {
  const neutrals = useDarkModeNeutrals();
  const buttonStyleObj = { borderRadius: buttonRadius };
  if (buttonStyle === 'outline') {
    buttonStyleObj.backgroundColor = 'transparent';
    buttonStyleObj.border = `2px solid ${buttonBgColor}`;
    buttonStyleObj.color = buttonBgColor;
  } else if (buttonStyle === 'ghost') {
    buttonStyleObj.backgroundColor = 'transparent';
    buttonStyleObj.color = buttonBgColor;
  } else {
    buttonStyleObj.backgroundColor = buttonBgColor;
    buttonStyleObj.color = buttonTextColor;
  }

  return (
    <section className="ts-hero-banner ts-hero-banner--playful">
      <div className="ts-hero-playful__shapes">
        <div className="ts-hero-playful__shape ts-hero-playful__shape--1" style={{ backgroundColor: buttonBgColor || colors.primary }} />
        <div className="ts-hero-playful__shape ts-hero-playful__shape--2" style={{ backgroundColor: colors.accent || '#ffd93d' }} />
        <div className="ts-hero-playful__shape ts-hero-playful__shape--3" style={{ backgroundColor: colors.secondary || '#6bcb77' }} />
      </div>
      <div className="ts-hero-playful__content">
        <h1 className="ts-hero-playful__title" style={{ color: titleColor !== '#ffffff' ? titleColor : neutrals.text }}>{title}</h1>
        {subtitle && <p className="ts-hero-playful__subtitle" style={{ color: subtitleColor !== '#e5e5e5' ? subtitleColor : neutrals.textMuted }}>{subtitle}</p>}
        {buttonText && (
          <Link to={buttonLink} className="ts-hero-playful__button" style={buttonStyleObj}>
            {buttonText}
          </Link>
        )}
      </div>
      {backgroundImage && (
        <div className="ts-hero-playful__image-wrapper">
          <img src={backgroundImage} alt="" className="ts-hero-playful__image" />
        </div>
      )}
    </section>
  );
}

// Luxury Hero - Elegant, centered, serif
function LuxeHero({ height, title, subtitle, buttonText, buttonLink, backgroundImage, fonts, titleColor, subtitleColor, buttonBgColor, buttonTextColor, buttonStyle, buttonRadius }) {
  const buttonStyleObj = { borderRadius: buttonRadius };
  if (buttonStyle === 'outline') {
    buttonStyleObj.backgroundColor = 'transparent';
    buttonStyleObj.border = `2px solid ${buttonBgColor}`;
    buttonStyleObj.color = buttonBgColor;
  } else if (buttonStyle === 'ghost') {
    buttonStyleObj.backgroundColor = 'transparent';
    buttonStyleObj.color = buttonBgColor;
  } else {
    buttonStyleObj.backgroundColor = buttonBgColor;
    buttonStyleObj.color = buttonTextColor;
  }

  return (
    <section className="ts-hero-banner ts-hero-banner--luxe" style={{ minHeight: height }}>
      {backgroundImage && <img src={backgroundImage} alt="" className="ts-hero-luxe__bg" />}
      <div className="ts-hero-luxe__overlay" />
      <div className="ts-hero-luxe__content">
        <div className="ts-hero-luxe__divider" style={{ backgroundColor: buttonBgColor }} />
        <h1 className="ts-hero-luxe__title" style={{ fontFamily: fonts.heading, color: titleColor }}>
          {title}
        </h1>
        {subtitle && <p className="ts-hero-luxe__subtitle" style={{ color: subtitleColor }}>{subtitle}</p>}
        {buttonText && (
          <Link to={buttonLink} className="ts-hero-luxe__button" style={buttonStyleObj}>
            {buttonText}
          </Link>
        )}
        <div className="ts-hero-luxe__divider" style={{ backgroundColor: buttonBgColor }} />
      </div>
    </section>
  );
}

// Restaurant Warm Hero - Warm tones, food-focused
function WarmHero({ height, title, subtitle, buttonText, buttonLink, backgroundImage, colors, titleColor, subtitleColor, buttonBgColor, buttonTextColor, buttonStyle, buttonRadius }) {
  const buttonStyleObj = { borderRadius: buttonRadius };
  if (buttonStyle === 'outline') {
    buttonStyleObj.backgroundColor = 'transparent';
    buttonStyleObj.border = `2px solid ${buttonBgColor}`;
    buttonStyleObj.color = buttonBgColor;
  } else if (buttonStyle === 'ghost') {
    buttonStyleObj.backgroundColor = 'transparent';
    buttonStyleObj.color = buttonBgColor;
  } else {
    buttonStyleObj.backgroundColor = buttonBgColor;
    buttonStyleObj.color = buttonTextColor;
  }

  return (
    <section className="ts-hero-banner ts-hero-banner--warm" style={{ minHeight: height }}>
      {backgroundImage && <img src={backgroundImage} alt="" className="ts-hero-warm__bg" />}
      <div className="ts-hero-warm__overlay" />
      <div className="ts-hero-warm__content">
        <h1 className="ts-hero-warm__title" style={{ color: titleColor }}>{title}</h1>
        {subtitle && <p className="ts-hero-warm__subtitle" style={{ color: subtitleColor }}>{subtitle}</p>}
        {buttonText && (
          <Link to={buttonLink} className="ts-hero-warm__button" style={buttonStyleObj}>
            {buttonText}
          </Link>
        )}
      </div>
    </section>
  );
}

export default React.memo(HeroBanner);
